import { getAuthHeaders } from "../../../utils/authHeaders";
import { bullionLobbyMatchIdsEqual } from "./bullionMatchLandingLobby";

const BULLION_TRADER_CRICKET_EVENTS_CACHE_TTL_MS = 20_000;

type CacheEntry = {
    expiresAt: number;
    payload: unknown[];
};

let cache: CacheEntry | null = null;
let inflight: Promise<unknown[]> | null = null;

/**
 * GET `/trade/cricket/events?maxmatches=100` — shared by Bullion lobby gap-fill when active plays
 * reference fixtures not in the frozen Bullion list. Short TTL + in-flight dedupe limits repeat calls
 * when multiple components or effects need the same snapshot.
 */
export async function fetchTraderCricketEventsCached(opts?: { bypassCache?: boolean }): Promise<unknown[]> {
    const now = Date.now();
    if (!opts?.bypassCache && cache && cache.expiresAt > now) {
        return cache.payload;
    }
    if (inflight) return inflight;

    inflight = (async () => {
        try {
            const url = `${process.env.API_URL}/trade/cricket/events?maxmatches=100`;
            const res = await fetch(url, { method: "GET", headers: getAuthHeaders() });
            if (!res.ok) return [];
            const data = await res.json().catch(() => ({}));
            const cricket = Array.isArray(data.matches) ? data.matches : [];
            const payload = cricket.map((match: any) => ({ ...match, sport: "cricket" }));
            if (!opts?.bypassCache) {
                cache = { expiresAt: Date.now() + BULLION_TRADER_CRICKET_EVENTS_CACHE_TTL_MS, payload };
            }
            return payload;
        } catch {
            return [];
        } finally {
            inflight = null;
        }
    })();

    return inflight;
}

export function invalidateTraderCricketEventsCache(): void {
    cache = null;
}

/** Match ids in `activeBullionGamesByMatchId` that are not yet present as row `id` on the lobby list. */
export function computeMissingActiveMatchIdsFromLobby(activeMids: string[], lobbyRows: unknown): string[] {
    const mids = (Array.isArray(activeMids) ? activeMids : []).map((k) => String(k || "").trim()).filter(Boolean);
    if (mids.length === 0) return [];
    const rows = Array.isArray(lobbyRows) ? lobbyRows : [];
    const lobbyIds = rows
        .map((r: { id?: unknown }) => String((r as { id?: unknown })?.id ?? "").trim())
        .filter(Boolean);
    return mids.filter((id) => !lobbyIds.some((lid) => bullionLobbyMatchIdsEqual(lid, id)));
}
